 "use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useOpenWithIntegrationCheck } from "../users/useOpenWithIntegrationCheck";
import {
  CalendarSource,
  ICalendar,
  ICalendarAuth,
  Organization,
  OrganizationSettings,
  ResponseWithStatus
} from "@gettactic/api";
import { getMSTwoWayUrl } from "@gettactic/helpers";
import { MS_1_MINUTE } from "../../defines";
export const useRoomReservation = ({
  isOpen,
  isRoomReservation: mayNeedDelegatedAccess,
  calendar,
  organizationSettings,
  slug,
  integrationsUrl,
  organizerId,
  currentUserId,
  apiClient
}: {
  isOpen: boolean;
  isRoomReservation: boolean;
  calendar: Pick<ICalendar, "source" | "is_enabled"> | null;
  organizationSettings: Pick<OrganizationSettings, "calendar_settings"> | null;
  slug: Organization["slug"];
  integrationsUrl: string;
  /** User ID of the reservation organizer, when it can be someone other than the current user. */
  organizerId?: string | null;
  currentUserId?: string;
  apiClient: {
    getMicrosoftPermissions: (
      asUserId?: string
    ) => Promise<ResponseWithStatus<ICalendarAuth>>;
  };
}) => {
  // A disabled calendar doesn't sync, so it's treated as no calendar at all
  const { hasMSCalendar, hasGoogleCalendar } = useMemo(() => {
    const enabledSource = calendar?.is_enabled ? calendar.source : null;
    return {
      hasMSCalendar: enabledSource === CalendarSource.Microsoft,
      hasGoogleCalendar: enabledSource === CalendarSource.Google
    };
  }, [calendar?.source, calendar?.is_enabled]);

  // The backend requires delegated access from the organizer, so when booking on
  // behalf of someone else it's their access that matters, not the current user's
  const otherOrganizerId =
    organizerId && organizerId !== currentUserId ? organizerId : undefined;

  const [requireDelegatedPermission, setRequireDelegatedPermission] =
    useState(false);
  const [
    organizerRequiresDelegatedAccess,
    setOrganizerRequiresDelegatedAccess
  ] = useState(false);
  // Who the last check was for, so changing the organizer checks again
  const checkedUserKey = useRef<string | null>(null);

  const msAuthUrl = getMSTwoWayUrl(integrationsUrl || "", slug || "");

  const shouldCheckForDelegatedAccess = useMemo(() => {
    return organizationSettings?.calendar_settings
      ?.microsoft_enable_delegated_user_access;
  }, [organizationSettings]);

  const { getShouldRequestDelegatedAccess } = useOpenWithIntegrationCheck({
    enabled:
      mayNeedDelegatedAccess &&
      hasMSCalendar &&
      shouldCheckForDelegatedAccess,
    asUserId: otherOrganizerId,
    apiClient,
    refetchInterval: MS_1_MINUTE
  });

  // Check permissions when modal opens for room reservations in creation mode
  useEffect(() => {
    const resetPermissions = () => {
      setRequireDelegatedPermission(false);
      setOrganizerRequiresDelegatedAccess(false);
      // Forget the last check too, otherwise the cleared result is never restored
      // when the calendar comes back (e.g. while switching rooms)
      checkedUserKey.current = null;
    };

    if (!isOpen) {
      // Reset when modal is closed
      resetPermissions();
      return;
    }

    // If the room does not have a Microsoft calendar, no permission needed
    if (!hasMSCalendar) {
      resetPermissions();
      return;
    }

    // For other than room creation reservations, no permission needed
    if (!mayNeedDelegatedAccess) {
      resetPermissions();
      return;
    }

    // Skip if already checked permissions for this organizer in this session
    const userKey = otherOrganizerId ?? "self";
    if (checkedUserKey.current === userKey) {
      return;
    }

    const checkPermissions = async () => {
      if (!shouldCheckForDelegatedAccess) {
        resetPermissions();
        return;
      }

      checkedUserKey.current = userKey;
      const shouldRequestDelegatedAccess =
        await getShouldRequestDelegatedAccess();

      // The organizer changed while checking, a newer check owns the result
      if (checkedUserKey.current !== userKey) {
        return;
      }

      // Only the current user can grant access for themselves, other organizers get a warning
      setRequireDelegatedPermission(
        !otherOrganizerId && shouldRequestDelegatedAccess
      );
      setOrganizerRequiresDelegatedAccess(
        !!otherOrganizerId && shouldRequestDelegatedAccess
      );
    };

    checkPermissions();
  }, [
    mayNeedDelegatedAccess,
    shouldCheckForDelegatedAccess,
    getShouldRequestDelegatedAccess,
    isOpen,
    hasMSCalendar,
    otherOrganizerId
  ]);

  return {
    requireDelegatedPermission,
    organizerRequiresDelegatedAccess,
    msAuthUrl,
    hasMSCalendar,
    hasGoogleCalendar
  };
};
